// Pure functions for the Analytics tab. No React, no Supabase. Deterministic.
// All formulas and shapes are defined in docs/superpowers/specs/2026-04-21-revenue-analytics-tab-design.md §4–§6.

// ─── Private helpers ─────────────────────────────────────────────────────────

function daysBetween(aIso, bIso) {
  const a = new Date(aIso + 'T12:00:00Z');
  const b = new Date(bIso + 'T12:00:00Z');
  return Math.round((b - a) / 86400000);
}

function addDays(iso, n) {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Clamp a "MM-DD" season marker to a real calendar day. Guards against
 * impossible dates that have been stored or entered (e.g. "09-31", "02-30"):
 * Safari/JavaScriptCore returns an Invalid Date for these and any downstream
 * `.toISOString()` throws, white-screening the whole Analytics page. Chrome
 * silently rolls them over, hiding the problem. Clamping the day to the
 * month's maximum makes every season marker a valid date on all engines.
 * Year 2001 is a non-leap reference, so 02-29 clamps to 02-28.
 */
export function clampMd(md) {
  const m = Number(md?.slice(0, 2));
  const d = Number(md?.slice(3, 5));
  if (!m || m < 1 || m > 12 || !d) return md;
  const maxDay = new Date(2001, m, 0).getDate();
  const day = Math.min(Math.max(d, 1), maxDay);
  return `${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ─── resolveSeasons ───────────────────────────────────────────────────────────

/**
 * Resolve "this season" and "last season" date ranges given a property's
 * season_start_md / season_end_md (MM-DD strings) and today (ISO date).
 *
 * Handles three cases:
 *   A: same-year season    (e.g. 04-01 → 10-31, coastal)
 *   B: cross-year season   (e.g. 12-01 → 03-31, ski)
 *   C: always-open         (01-01 → 12-31)
 */
export function resolveSeasons(property, today) {
  const startMd = clampMd(property.season_start_md || '04-01');
  const endMd   = clampMd(property.season_end_md   || '10-31');
  const year    = Number(today.slice(0, 4));
  const md      = today.slice(5);

  const crossYear = startMd > endMd;
  const mk = (y, m) => `${y}-${m}`;

  if (!crossYear) {
    // Case A / Case C — season is contained within one calendar year
    if (md < startMd) {
      // Before this year's season starts → most recent completed season was last year's
      return {
        thisSeason: { start: mk(year - 1, startMd), end: mk(year - 1, endMd) },
        lastSeason: { start: mk(year - 2, startMd), end: mk(year - 2, endMd) },
      };
    }
    // During or after this year's season
    return {
      thisSeason: { start: mk(year, startMd), end: mk(year, endMd) },
      lastSeason: { start: mk(year - 1, startMd), end: mk(year - 1, endMd) },
    };
  }

  // Case B — cross-year season (startMd > endMd lexicographically, e.g. 12-01 > 03-31)
  if (md >= startMd) {
    // In the "opening" half of the season (e.g. December)
    return {
      thisSeason: { start: mk(year, startMd),     end: mk(year + 1, endMd) },
      lastSeason: { start: mk(year - 1, startMd), end: mk(year,     endMd) },
    };
  }
  if (md <= endMd) {
    // In the "closing" half of the season (e.g. February, March)
    return {
      thisSeason: { start: mk(year - 1, startMd), end: mk(year,     endMd) },
      lastSeason: { start: mk(year - 2, startMd), end: mk(year - 1, endMd) },
    };
  }
  // Between seasons (e.g. August for a Dec–Mar ski season) → most recent completed
  return {
    thisSeason: { start: mk(year - 1, startMd), end: mk(year,     endMd) },
    lastSeason: { start: mk(year - 2, startMd), end: mk(year - 1, endMd) },
  };
}

// ─── seasonMonthRanges ────────────────────────────────────────────────────────

/** Last day of the calendar month containing `iso`, as an ISO date string. */
function lastOfMonthIso(iso) {
  const [y, m] = iso.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${iso.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

/**
 * Expand a resolved season { start, end } into the ordered list of full
 * calendar months it spans. Each entry is { start, end } covering the whole
 * calendar month (first day → last day), even when the season begins or ends
 * mid-month. Works for same-year (May–Sep) and cross-year (Dec–Mar) seasons.
 * Always returns at least one month.
 */
export function seasonMonthRanges(season) {
  const out = [];
  let cursor = season.start.slice(0, 7) + '-01'; // first of the start month
  while (cursor <= season.end) {
    const end = lastOfMonthIso(cursor);
    out.push({ start: cursor, end });
    cursor = addDays(end, 1);
  }
  return out;
}

// ─── buildRoomNights ──────────────────────────────────────────────────────────

/**
 * Expand confirmed bookings into a flat array of per-night records.
 * Each record carries denormalised financial fields so downstream
 * aggregations only need to sum/filter — no further logic required.
 */
export function buildRoomNights(bookings, rooms, property) {
  const roomById = new Map(rooms.map(r => [r.id, r]));
  const out = [];

  for (const b of bookings) {
    if (b.status !== 'confirmed') continue;
    const room = roomById.get(b.room_id);
    if (!room) continue;

    const nights = daysBetween(b.check_in, b.check_out);
    if (nights <= 0) continue;

    const isBcom        = b.source === 'Booking.com';
    const trackOta      = property?.track_ota_commission !== false;
    const commissionPct = b.commission_pct_snapshot != null
      ? Number(b.commission_pct_snapshot)
      : (isBcom && trackOta ? Number(property?.bcom_commission)  || 0 : 0);
    const vccFeePct     = b.vcc_fee_pct_snapshot != null
      ? Number(b.vcc_fee_pct_snapshot)
      : (isBcom && trackOta ? Number(property?.bcom_vcc_fee_pct) || 0 : 0);
    const extraBedPx    = b.extra_bed_price_snapshot != null
      ? Number(b.extra_bed_price_snapshot)
      : Number(property?.extra_bed_prices?.[room.type]) || 0;

    const price              = Number(b.price) || 0;
    const breakfastTotal     = Number(b.breakfast_price) || 0;
    const extraBedsCount     = Number(b.extra_beds) || 0;
    const extraBedsTotalStay = extraBedPx * extraBedsCount * nights;

    const nightlyGross      = price / nights;
    const nightlyCommission = nightlyGross * (commissionPct / 100);
    const nightlyVccFee     = nightlyGross * (vccFeePct / 100);
    const nightlyOtaCost    = nightlyCommission + nightlyVccFee;
    const nightlyNet        = nightlyGross - nightlyOtaCost;
    const nightlyBreakfast  = breakfastTotal / nights;
    const nightlyExtraBeds  = extraBedsTotalStay / nights;
    const nightlyRoomOnly   = nightlyGross - nightlyBreakfast - nightlyExtraBeds;

    const createdAt  = b.created_at || b.check_in + 'T00:00:00Z';
    const createdIso = createdAt.slice(0, 10);

    for (let i = 0; i < nights; i++) {
      const date = addDays(b.check_in, i);
      out.push({
        date,
        roomId:           room.id,
        roomType:         room.type,
        bookingId:        b.id,
        source:           b.source || 'Other',
        createdAt,
        status:           'confirmed',
        nights,
        nightlyGross,
        nightlyCommission,
        nightlyVccFee,
        nightlyOtaCost,
        nightlyNet,
        nightlyBreakfast,
        nightlyExtraBeds,
        nightlyRoomOnly,
        leadDaysAtCreate: daysBetween(createdIso, date),
        isFirstNight:     i === 0,
      });
    }
  }
  return out;
}

// ─── filterPeriod ─────────────────────────────────────────────────────────────

/** Keep only room-night records whose date falls within [start, end] inclusive. */
export function filterPeriod(roomNights, start, end) {
  return roomNights.filter(n => n.date >= start && n.date <= end);
}

// ─── sumRevenue ───────────────────────────────────────────────────────────────

/** Sum net (default) or gross revenue across an array of room-night records. */
export function sumRevenue(roomNights, mode = 'net') {
  const key = mode === 'gross' ? 'nightlyGross' : 'nightlyNet';
  let sum = 0;
  for (const n of roomNights) sum += n[key];
  return sum;
}

// ─── occupancyPct ─────────────────────────────────────────────────────────────

/**
 * Occupancy percentage = occupied room-nights / total available room-nights.
 * Returns null when the denominator is zero (no rooms or no days).
 */
export function occupancyPct(roomNights, rooms, days) {
  if (!rooms || rooms.length === 0 || !days || days <= 0) return null;
  return (roomNights.length / (rooms.length * days)) * 100;
}

// ─── adr ─────────────────────────────────────────────────────────────────────

/**
 * Average Daily Rate = total revenue / occupied nights.
 * Returns null for an empty set.
 */
export function adr(roomNights, mode = 'net') {
  if (!roomNights || roomNights.length === 0) return null;
  return sumRevenue(roomNights, mode) / roomNights.length;
}

// ─── paceCurve ────────────────────────────────────────────────────────────────

/**
 * Build a weekly pace curve showing how many room-nights within `target`
 * were on the books at each snapshot date in [sweepStart, sweepEnd].
 *
 * @param {object[]} roomNights  - Already filtered to the target season/period
 * @param {{ start: string, end: string }} target  - The period whose nights we track
 * @param {string} sweepStart   - ISO date: first snapshot
 * @param {string} sweepEnd     - ISO date: last snapshot (typically season start)
 * @param {number} capacityNights - Denominator for pct (e.g. rooms × days)
 * @returns {{ date: string, onBooks: number, pct: number }[]}
 */
export function paceCurve(roomNights, target, sweepStart, sweepEnd, capacityNights) {
  const inTarget = roomNights.filter(n => n.date >= target.start && n.date <= target.end);
  const points = [];
  let cursor = sweepStart;
  while (cursor <= sweepEnd) {
    const onBooks = inTarget.filter(n => n.createdAt.slice(0, 10) <= cursor).length;
    points.push({
      date: cursor,
      onBooks,
      pct: capacityNights > 0 ? (onBooks / capacityNights) * 100 : 0,
    });
    cursor = addDays(cursor, 7);
  }
  return points;
}

// ─── hasPaceMeaning ──────────────────────────────────────────────────────────

/**
 * Returns true when a pace curve has enough weekly variation to be worth
 * displaying as a sparkline. Bookings entered all at once (bulk import) produce
 * only 2 distinct onBooks values (0 → N); organically accumulated bookings
 * produce many intermediate values. Requiring ≥3 distinct non-zero values is
 * a simple proxy for "data arrived across multiple separate weeks."
 */
export function hasPaceMeaning(curve) {
  const nonZeroValues = new Set(curve.map(p => p.onBooks).filter(v => v > 0));
  return nonZeroValues.size >= 3;
}

// ─── bucketLos & bucketLeadTime ───────────────────────────────────────────────

export const LOS_BUCKETS = ['1-2', '3-4', '5-7', '7+'];
export const LT_BUCKETS  = ['0', '1-3', '4-7', '8-14', '15-30', '31-60', '61+'];

function losBucket(n) {
  if (n <= 2) return '1-2';
  if (n <= 4) return '3-4';
  if (n <= 7) return '5-7';
  return '7+';
}

function ltBucket(d) {
  if (d === 0)  return '0';
  if (d <= 3)   return '1-3';
  if (d <= 7)   return '4-7';
  if (d <= 14)  return '8-14';
  if (d <= 30)  return '15-30';
  if (d <= 60)  return '31-60';
  return '61+';
}

/** Count bookings by length-of-stay bucket. */
export function bucketLos(bookings) {
  const out = Object.fromEntries(LOS_BUCKETS.map(k => [k, 0]));
  for (const b of bookings) {
    const n = daysBetween(b.check_in, b.check_out);
    if (n > 0) out[losBucket(n)] += 1;
  }
  return out;
}

/** Count bookings by lead-time (days between created_at and check_in) bucket. */
export function bucketLeadTime(bookings) {
  const out = Object.fromEntries(LT_BUCKETS.map(k => [k, 0]));
  for (const b of bookings) {
    if (!b.created_at) continue;
    const d = daysBetween(b.created_at.slice(0, 10), b.check_in);
    if (d >= 0) out[ltBucket(d)] += 1;
  }
  return out;
}

// ─── collapseSmallSources ─────────────────────────────────────────────────────

/**
 * Merge sources whose share of total revenue falls below `threshold`
 * into an "Other" bucket. Leaves large sources untouched.
 */
export function collapseSmallSources(bySource, threshold = 0.05) {
  const total = Object.values(bySource).reduce((s, v) => s + v, 0);
  if (total === 0) return { ...bySource };
  const out = {};
  let other = 0;
  for (const [k, v] of Object.entries(bySource)) {
    if (v / total < threshold) other += v;
    else out[k] = v;
  }
  if (other > 0) out.Other = (out.Other || 0) + other;
  return out;
}

// ─── periodPresets ────────────────────────────────────────────────────────────

/**
 * Return a set of common period presets for the period-picker UI.
 * All date strings are ISO (YYYY-MM-DD).
 */
export function periodPresets(property, today) {
  const seasons  = resolveSeasons(property, today);
  const year     = today.slice(0, 4);
  const month    = today.slice(5, 7);
  const lastDay  = new Date(Number(year), Number(month), 0).getDate();
  return {
    thisSeason: seasons.thisSeason,
    lastSeason: seasons.lastSeason,
    thisMonth:  { start: `${year}-${month}-01`, end: `${year}-${month}-${String(lastDay).padStart(2, '0')}` },
    last30:     { start: addDays(today, -29), end: today },
    ytd:        { start: `${year}-01-01`, end: today },
  };
}
